<script lang="ts">
    import { tick, untrack } from 'svelte'

    import Icon from '@iconify/svelte'

    import type { CrumbArray } from 'hydria/components/breadcrumb'
    import { iconExclamationTriangle } from 'hydria/icons'
    import { gettext as _ } from 'hydria/services/gettext'
    import { notify } from 'hydria/services/notifications'
    import { simpleFetch, groups as ugroups } from 'hydria/services/session'
    import type { SimpleFetchResult } from 'hydria/services/session'
    import { spath } from 'hydria/svelte_helpers'
    import { toLuxon } from 'hydria/utils/time'

    import { Breadcrumb } from 'breadcrumb'

    import type { AdminUser, UserStatus } from './user'

    import UserDelete from './UserDelete.svelte'
    import UserInfo from './UserInfo.svelte'
    import UserLog from './UserLog.svelte'
    import UserPermissions from './UserPermissions.svelte'

    interface Props {
        route: any
    }

    let { route = {} }: Props = $props()

    let status: UserStatus | undefined = $state()
    let userId: string = $derived(route.result.path.params?.id)
    let user: AdminUser = $state(null as unknown as AdminUser)
    let error: string | undefined

    let loadingUserId: string | undefined = undefined
    // "Signal": causer un reload des sous-elements si > 0
    let reloadInc: number = $state(0)

    async function loadUser(userId: string) {
        let r: SimpleFetchResult

        status = 'loading'
        loadingUserId = userId
        error = undefined

        try {
            r = await simpleFetch('GET', `/json/users/1/${userId}`)
        } catch (e: any) {
            r = e
            console.error('Failed to load user data:', e)
            notify({
                type: 'danger',
                title: $_('Utilisateur administratif'),
                text: 'L’utilisateur n’a pas pu être chargé' + (r.data.error ? ` : ${r.data.error}` : '.'),
            })
            status = r.status == 404 ? 'not-found' : 'error'
            error = r.data.error ? r.data.error : 'Unknown error'
            return
        }
        if (loadingUserId !== userId) {
            console.warn(`Done loading user ${userId}, but now expecting ${loadingUserId}; dropping result.`)
            return
        }

        importUserData(r.data)

        status = 'ready'
    }

    function importUserData(data: any) {
        const u = data.item

        for (const k of ['ctime', 'mtime', 'last_signed_in']) {
            if (u[k]) u[k] = toLuxon(u[k])
        }

        user = u
        reloadInc = untrack(() => reloadInc) + 1
    }

    const crumbs = $derived.by(() => {
        const cs: CrumbArray = [{ txt: $_('Utilisateurs du site administratif'), url: spath('/admin-users') }]
        switch (status) {
            case 'ready':
            case 'saving':
                cs.push({ txt: user ? user.email : '…' })
                break
            case null:
            case 'loading':
                cs.push({ txt: '…' })
                break
            case 'not-found':
            case 'error':
                cs.push({ txt: iconExclamationTriangle, html: true })
                break
        }

        return cs
    })

    $effect(() => {
        tick().then(() => {
            loadUser(userId)
        })
    })
</script>

<Breadcrumb {crumbs} />

{#if status == 'not-found' || status == 'error'}
    <div class="alert alert-danger">
        <Icon inline icon="fa:exclamation-triangle" />
        {#if status == 'not-found'}
            {$_('The requested document does not exist.')}
        {:else}
            {$_('An error occured. Please try again in a moment.')}
        {/if}
    </div>
{:else if status == null}
    <div class="card">
        <div class="card-body">
            <div class="m-4 d-flex justify-content-center align-items-center">
                <div class="spinner-border me-3" role="status" aria-hidden="true"></div>
            </div>
        </div>
    </div>
{:else if user != null}
    <div class="row mb-4">
        <div class="col col-sm-6 col-md-8 col-lg-8 d-flex">
            <UserPermissions
                {status}
                {user}
                setLockedState={(nv) => {
                    user.locked = nv
                    reloadInc++
                }}
                userImportData={(udata) => {
                    importUserData(udata)
                }}
            />
        </div>
        <div class="col col-sm-6 col-md-4 col-lg-4 d-flex flex-column gap-4">
            <UserInfo {status} {user} />
            {#if status == 'ready' && !user.active && $ugroups.has('global-user-admin') && $ugroups.has('can-edit-user-capabilities')}
                <UserDelete {user} />
            {/if}
        </div>
    </div>
    <UserLog {userId} {status} reload={reloadInc} />
{/if}
